import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import api from "../../../api/api";
import Swal from "sweetalert2";

import {
  Modal,
  Input,
  Button,
  Select,
  Spin,
  Typography,
  Tag,
} from "antd";

import {
  QrcodeOutlined,
  CameraOutlined,
  CloseOutlined,
  ReloadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CrownOutlined,
  GiftOutlined,
  TrophyOutlined,
  ShoppingOutlined,
  HistoryOutlined,
  CalendarOutlined,
  SafetyOutlined,
  ScanOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";

import { BrowserQRCodeReader } from "@zxing/browser";


import WELCOME_AUDIO from "./audio/femalevoicewelcome.mp3";
import SUCCESS_AUDIO from "./audio/sucessess.mp3";
import ERROR_AUDIO from "./audio/erroror.mp3";

const { Title, Text } = Typography;

// ============================================================
// ROYALTY ATM SCANNER
// ============================================================

const RoyaltyATMScan = () => {
  // ==========================================================
  // SCANNER STATE
  // ==========================================================

  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerError, setScannerError] = useState("");

  const [cameraDevices, setCameraDevices] = useState([]);
  const [selectedCameraId, setSelectedCameraId] = useState("");
  const [cameraSwitching, setCameraSwitching] = useState(false);

  const [scanStatus, setScanStatus] = useState("ready");

  // ==========================================================
  // CUSTOMER QR
  // ==========================================================

  const [qrToken, setQrToken] = useState("");

  // ==========================================================
  // PIN
  // ==========================================================

  const [pinModalOpen, setPinModalOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [checkingCustomer, setCheckingCustomer] = useState(false);

  // ==========================================================
  // CUSTOMER
  // ==========================================================

  const [customer, setCustomer] = useState(null);

  // ==========================================================
  // REFS
  // ==========================================================

  const videoRef = useRef(null);

  const scannerReaderRef = useRef(null);
  const scannerControlsRef = useRef(null);

  const mountedRef = useRef(true);

  const processingScanRef = useRef(false);

  const lastScannedTokenRef = useRef("");
  const lastScanTimeRef = useRef(0);

    const successAudioRef = useRef(null);
    const errorAudioRef = useRef(null);
    const welcomeAudioRef = useRef(null);

  const startTimeoutRef = useRef(null);

  const duplicateDelay = 1200;

  // ==========================================================
  // AUDIO INITIALIZATION
  // ==========================================================

//   useEffect(() => {
//     const successAudio = new Audio(SUCCESS_AUDIO);
//     const errorAudio = new Audio(ERROR_AUDIO);

//     successAudio.preload = "auto";
//     errorAudio.preload = "auto";

//     successAudioRef.current = successAudio;
//     errorAudioRef.current = errorAudio;

//     return () => {
//       try {
//         successAudio.pause();
//         successAudio.currentTime = 0;
//       } catch {}

//       try {
//         errorAudio.pause();
//         errorAudio.currentTime = 0;
//       } catch {}

//       successAudioRef.current = null;
//       errorAudioRef.current = null;
//     };
//   }, []);


// ==========================================================
// AUDIO INITIALIZATION
// ==========================================================

useEffect(() => {
  const successAudio = new Audio(SUCCESS_AUDIO);
  const errorAudio = new Audio(ERROR_AUDIO);
  const welcomeAudio = new Audio(WELCOME_AUDIO);

  successAudio.preload = "auto";
  errorAudio.preload = "auto";
  welcomeAudio.preload = "auto";

  successAudioRef.current = successAudio;
  errorAudioRef.current = errorAudio;
  welcomeAudioRef.current = welcomeAudio;

  return () => {
    try {
      successAudio.pause();
      successAudio.currentTime = 0;
    } catch {}

    try {
      errorAudio.pause();
      errorAudio.currentTime = 0;
    } catch {}

    try {
      welcomeAudio.pause();
      welcomeAudio.currentTime = 0;
    } catch {}

    successAudioRef.current = null;
    errorAudioRef.current = null;
    welcomeAudioRef.current = null;
  };
}, []);


// ==========================================================
// WELCOME VOICE
// ==========================================================

const playWelcomeSound = useCallback(() => {
  try {
    const audio = welcomeAudioRef.current;

    if (!audio) {
      return;
    }

    audio.currentTime = 0;

    const promise = audio.play();

    if (promise?.catch) {
      promise.catch((error) => {
        console.debug(
          "Welcome audio playback blocked:",
          error
        );
      });
    }
  } catch (error) {
    console.debug(
      "Welcome audio error:",
      error
    );
  }
}, []);

  // ==========================================================
  // SUCCESS SOUND
  // ==========================================================

  const playSuccessSound = useCallback(() => {
    try {
      const audio = successAudioRef.current;

      if (!audio) return;

      audio.currentTime = 0;

      const promise = audio.play();

      if (promise?.catch) {
        promise.catch(() => {});
      }
    } catch {}
  }, []);

  // ==========================================================
  // ERROR SOUND
  // ==========================================================

  const playErrorSound = useCallback(() => {
    try {
      const audio = errorAudioRef.current;

      if (!audio) return;

      audio.currentTime = 0;

      const promise = audio.play();

      if (promise?.catch) {
        promise.catch(() => {});
      }
    } catch {}
  }, []);

  // ==========================================================
  // FORMAT NUMBER
  // ==========================================================

  const formatNumber = useCallback((value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "0";
    }

    return number.toLocaleString("en-US");
  }, []);

  // ==========================================================
  // FORMAT MONEY
  // ==========================================================

  const formatMoney = useCallback((value) => {
    const number = Number(value);

    if (!Number.isFinite(number)) {
      return "$0.00";
    }

    return `$${number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }, []);

// ==========================================================
// CUSTOMER REWARD BALANCE
// ==========================================================

const getCustomerRewardBalance = useCallback((customerData) => {
  if (!customerData) {
    return 0;
  }

  // --------------------------------------------------------
  // Best available reward
  // --------------------------------------------------------

  const reward =
    customerData.rewards_available;

  if (
    reward &&
    typeof reward === "object"
  ) {
    const amount = Number(
      reward.amount
    );

    if (Number.isFinite(amount)) {
      return amount;
    }
  }

  // --------------------------------------------------------
  // Fallback
  // --------------------------------------------------------

  return 0;
}, []);

  // ==========================================================
  // FORMAT DATE
  // ==========================================================

  const formatDate = useCallback((value) => {
    if (!value) {
      return "—";
    }

    try {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return String(value);
    }
  }, []);

  // ==========================================================
  // FORMAT DATE TIME
  // ==========================================================

  const formatDateTime = useCallback((value) => {
    if (!value) {
      return "—";
    }

    try {
      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return String(value);
      }

      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return String(value);
    }
  }, []);

  // ==========================================================
  // EXTRACT QR TOKEN
  // ==========================================================

  const extractQRToken = useCallback((rawValue) => {
    if (!rawValue) {
      return null;
    }

    const value = String(rawValue).trim();

    if (!value) {
      return null;
    }

    // --------------------------------------------------------
    // JSON QR
    // --------------------------------------------------------

    try {
      const parsed = JSON.parse(value);

      if (parsed && typeof parsed === "object") {
        const possibleKeys = [
          "card_qr_token",
          "qr_token",
          "token",
          "card_token",
        ];

        for (const key of possibleKeys) {
          if (parsed[key]) {
            return String(parsed[key]).trim();
          }
        }

        if (
          parsed.type === "customer" &&
          parsed.token
        ) {
          return String(parsed.token).trim();
        }
      }
    } catch {}

    // --------------------------------------------------------
    // URL QR
    // --------------------------------------------------------

    try {
      const url = new URL(value);

      const possibleParameters = [
        "card_qr_token",
        "qr_token",
        "token",
        "card_token",
      ];

      for (const parameter of possibleParameters) {
        const token =
          url.searchParams.get(parameter);

        if (token) {
          return token.trim();
        }
      }

      // If the QR URL itself contains the token in the path.
      const pathParts = url.pathname
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean);

      const lastPathPart =
        pathParts[pathParts.length - 1];

      if (lastPathPart) {
        const uuidInPath =
          lastPathPart.match(
            /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
          );

        if (uuidInPath) {
          return uuidInPath[0];
        }
      }
    } catch {}

    // --------------------------------------------------------
    // UUID
    // --------------------------------------------------------

    const uuidMatch = value.match(
      /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i
    );

    if (uuidMatch) {
      return uuidMatch[0];
    }

    // --------------------------------------------------------
    // RAW TOKEN
    // --------------------------------------------------------

    return value;
  }, []);

  // ==========================================================
  // STOP SCANNER
  // ==========================================================

  const stopScanner = useCallback(() => {
    if (startTimeoutRef.current) {
      window.clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }

    // --------------------------------------------------------
    // ZXING CONTROLS
    // --------------------------------------------------------

    try {
      if (scannerControlsRef.current) {
        scannerControlsRef.current.stop();
      }
    } catch (error) {
      console.debug(
        "Scanner controls stop error:",
        error
      );
    }

    scannerControlsRef.current = null;

    // --------------------------------------------------------
    // ZXING READER
    // --------------------------------------------------------

    try {
      if (scannerReaderRef.current) {
        scannerReaderRef.current.reset();
      }
    } catch (error) {
      console.debug(
        "Scanner reader reset error:",
        error
      );
    }

    scannerReaderRef.current = null;

    // --------------------------------------------------------
    // VIDEO STREAM
    // --------------------------------------------------------

    try {
      const video = videoRef.current;

      if (video?.srcObject) {
        const stream = video.srcObject;

        if (stream?.getTracks) {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch {}
          });
        }

        video.srcObject = null;
      }
    } catch (error) {
      console.debug(
        "Video stream cleanup error:",
        error
      );
    }

    processingScanRef.current = false;

    if (mountedRef.current) {
      setScannerLoading(false);
      setCameraSwitching(false);
      setScanStatus("ready");
    }
  }, []);

  // ==========================================================
  // FIND PREFERRED CAMERA
  // ==========================================================

  const findPreferredCamera = useCallback(
    (devices) => {
      if (!devices?.length) {
        return null;
      }

      // Keep selected camera when still available.
      if (selectedCameraId) {
        const selected = devices.find(
          (device) =>
            device.deviceId === selectedCameraId
        );

        if (selected) {
          return selected;
        }
      }

      // ------------------------------------------------------
      // Prefer rear camera.
      // ------------------------------------------------------

      const rearCamera = devices.find((device) => {
        const label =
          device.label?.toLowerCase() || "";

        return (
          label.includes("back") ||
          label.includes("rear") ||
          label.includes("environment") ||
          label.includes("facing back") ||
          label.includes("world")
        );
      });

      if (rearCamera) {
        return rearCamera;
      }

      // ------------------------------------------------------
      // Otherwise first available camera.
      // ------------------------------------------------------

      return devices[0];
    },
    [selectedCameraId]
  );

  // ==========================================================
  // LOAD CAMERA DEVICES
  // ==========================================================

  const loadCameraDevices = useCallback(async () => {
    try {
      // ------------------------------------------------------
      // Make sure browser supports camera API.
      // ------------------------------------------------------

      if (
        !navigator.mediaDevices ||
        !navigator.mediaDevices.getUserMedia
      ) {
        throw new Error(
          "Camera access is not supported by this browser."
        );
      }

      let temporaryStream = null;

      // ------------------------------------------------------
      // IMPORTANT:
      // Request camera permission FIRST.
      //
      // This makes camera labels/device IDs available on
      // browsers that hide them until permission is granted.
      // ------------------------------------------------------

      try {
        temporaryStream =
          await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: {
                ideal: "environment",
              },
            },
            audio: false,
          });
      } finally {
        if (temporaryStream) {
          temporaryStream
            .getTracks()
            .forEach((track) => {
              try {
                track.stop();
              } catch {}
            });
        }
      }

      // ------------------------------------------------------
      // Ask ZXing for all video devices.
      // ------------------------------------------------------

      const devices =
        await BrowserQRCodeReader.listVideoInputDevices();

      const videoDevices = devices.filter(
        (device) =>
          device.kind === "videoinput" ||
          !device.kind
      );

      if (!videoDevices.length) {
        throw new Error(
          "No camera was found on this device."
        );
      }

      if (mountedRef.current) {
        setCameraDevices(videoDevices);
      }

      // ------------------------------------------------------
      // Choose preferred camera.
      // ------------------------------------------------------

      const preferred =
        findPreferredCamera(videoDevices);

      if (
        preferred &&
        mountedRef.current
      ) {
        setSelectedCameraId(
          preferred.deviceId
        );
      }

      return videoDevices;
    } catch (error) {
      console.error(
        "CAMERA DEVICE ERROR:",
        error
      );

      throw error;
    }
  }, [findPreferredCamera]);

  // ==========================================================
  // HANDLE CUSTOMER QR
  // ==========================================================

  const handleCustomerQR = useCallback(
    (rawValue) => {
      const token =
        extractQRToken(rawValue);

      if (!token) {
        return;
      }

      const now = Date.now();

      // ------------------------------------------------------
      // Prevent duplicate callback processing.
      // ------------------------------------------------------

      if (processingScanRef.current) {
        return;
      }

      // ------------------------------------------------------
      // Prevent immediate duplicate scans.
      // ------------------------------------------------------

      if (
        lastScannedTokenRef.current === token &&
        now - lastScanTimeRef.current <
          duplicateDelay
      ) {
        return;
      }

      processingScanRef.current = true;

      lastScannedTokenRef.current = token;
      lastScanTimeRef.current = now;

      console.log(
        "ROYALTY QR DETECTED:",
        token
      );

setScanStatus("success");
setQrToken(token);
playWelcomeSound();
stopScanner();

      // ------------------------------------------------------
      // Move to PIN modal.
      // ------------------------------------------------------

      window.setTimeout(() => {
        if (!mountedRef.current) {
          return;
        }

        setScannerOpen(false);

        setPin("");
        setShowPin(false);

        setPinModalOpen(true);

        processingScanRef.current = false;
      }, 300);
    },
    [
      extractQRToken,
      playSuccessSound,
      stopScanner,
    ]
  );

  // ==========================================================
  // START SCANNER
  // ==========================================================

  // const startScanner = useCallback(
  //   async (requestedCameraId = null) => {
  //     try {
  //       setScannerLoading(true);
  //       setScannerError("");
  //       setScanStatus("processing");

  //       // ----------------------------------------------------
  //       // Stop previous scanner.
  //       // ----------------------------------------------------

  //       stopScanner();

  //       // ----------------------------------------------------
  //       // Give React time to restore the video element.
  //       // ----------------------------------------------------

  //       await new Promise((resolve) =>
  //         window.setTimeout(resolve, 50)
  //       );

  //       if (!videoRef.current) {
  //         throw new Error(
  //           "Camera video element is not ready."
  //         );
  //       }

  //       // ----------------------------------------------------
  //       // Load cameras if necessary.
  //       // ----------------------------------------------------

  //       let devices = cameraDevices;

  //       if (!devices.length) {
  //         devices =
  //           await loadCameraDevices();
  //       }

  //       if (!devices.length) {
  //         throw new Error(
  //           "No camera was found on this device."
  //         );
  //       }

  //       // ----------------------------------------------------
  //       // Determine camera.
  //       // ----------------------------------------------------

  //       let cameraId =
  //         requestedCameraId ||
  //         selectedCameraId;

  //       const cameraExists = devices.some(
  //         (device) =>
  //           device.deviceId === cameraId
  //       );

  //       if (!cameraExists) {
  //         const preferred =
  //           findPreferredCamera(devices);

  //         cameraId =
  //           preferred?.deviceId ||
  //           devices[0]?.deviceId;
  //       }

  //       if (!cameraId) {
  //         throw new Error(
  //           "Unable to select a camera."
  //         );
  //       }

  //       if (mountedRef.current) {
  //         setSelectedCameraId(cameraId);
  //       }

  //       // ----------------------------------------------------
  //       // Create ZXing reader.
  //       // ----------------------------------------------------

  //       const reader =
  //         new BrowserQRCodeReader();

  //       scannerReaderRef.current = reader;

  //       // ----------------------------------------------------
  //       // Start camera + QR decoding.
  //       //
  //       // IMPORTANT:
  //       // Use the actual selected deviceId.
  //       // ----------------------------------------------------

  //       const controls =
  //         await reader.decodeFromVideoDevice(
  //           cameraId,
  //           videoRef.current,
  //           (result, error) => {
  //             if (result) {
  //               const text =
  //                 result.getText();

  //               if (text) {
  //                 handleCustomerQR(text);
  //               }
  //             }

  //             // NotFoundException is normal while the camera
  //             // is searching for a QR code.
  //             if (
  //               error &&
  //               error.name !==
  //                 "NotFoundException"
  //             ) {
  //               console.debug(
  //                 "ZXing scanner:",
  //                 error
  //               );
  //             }
  //           }
  //         );

  //       scannerControlsRef.current =
  //         controls;

  //       // ----------------------------------------------------
  //       // Make absolutely sure video plays.
  //       // ----------------------------------------------------

  //       try {
  //         if (videoRef.current) {
  //           videoRef.current.muted = true;
  //           videoRef.current.playsInline = true;

  //           await videoRef.current.play();
  //         }
  //       } catch (playError) {
  //         console.debug(
  //           "Video play warning:",
  //           playError
  //         );
  //       }

  //       if (mountedRef.current) {
  //         setScanStatus("ready");
  //       }
  //     } catch (error) {
  //       console.error(
  //         "SCANNER START ERROR:",
  //         error
  //       );

  //       let message =
  //         "Unable to start the camera.";

  //       if (
  //         error?.name ===
  //         "NotAllowedError"
  //       ) {
  //         message =
  //           "Camera permission was denied. Please allow camera access in your browser settings and try again.";
  //       } else if (
  //         error?.name ===
  //         "NotFoundError"
  //       ) {
  //         message =
  //           "No camera was found on this device.";
  //       } else if (
  //         error?.name ===
  //         "NotReadableError"
  //       ) {
  //         message =
  //           "The camera is already being used by another application.";
  //       } else if (
  //         error?.name ===
  //         "OverconstrainedError"
  //       ) {
  //         message =
  //           "The selected camera is not available. Please select another camera.";
  //       } else if (
  //         error?.name ===
  //         "SecurityError"
  //       ) {
  //         message =
  //           "Camera access is blocked by browser security settings.";
  //       } else if (
  //         error?.name ===
  //         "AbortError"
  //       ) {
  //         message =
  //           "Camera startup was interrupted. Please try again.";
  //       } else if (
  //         error?.message
  //       ) {
  //         message = error.message;
  //       }

  //       if (mountedRef.current) {
  //         setScannerError(message);
  //         setScanStatus("error");
  //       }

  //       playErrorSound();

  //       // Clean partially started scanner.
  //       try {
  //         if (
  //           scannerControlsRef.current
  //         ) {
  //           scannerControlsRef.current.stop();
  //         }
  //       } catch {}

  //       scannerControlsRef.current = null;
  //     } finally {
  //       if (mountedRef.current) {
  //         setScannerLoading(false);
  //         setCameraSwitching(false);
  //       }
  //     }
  //   },
  //   [
  //     cameraDevices,
  //     findPreferredCamera,
  //     handleCustomerQR,
  //     loadCameraDevices,
  //     playErrorSound,
  //     selectedCameraId,
  //     stopScanner,
  //   ]
  // );


const startScanner = useCallback(
  async (requestedCameraId = null) => {
    try {
      setScannerLoading(true);
      setScannerError("");
      setScanStatus("processing");

      // ======================================================
      // STOP PREVIOUS CAMERA
      // ======================================================

      stopScanner();

      await new Promise((resolve) => {
        window.setTimeout(resolve, 150);
      });

      // ======================================================
      // VIDEO ELEMENT
      // ======================================================

      const video = videoRef.current;

      if (!video) {
        throw new Error(
          "Camera video element is not ready."
        );
      }

      // ======================================================
      // BASIC VIDEO SETTINGS
      // ======================================================

      video.autoplay = true;
      video.muted = true;
      video.playsInline = true;

      video.setAttribute(
        "autoplay",
        "true"
      );

      video.setAttribute(
        "muted",
        "true"
      );

      video.setAttribute(
        "playsinline",
        "true"
      );

      // ======================================================
      // LOAD CAMERAS
      // ======================================================

      let devices = cameraDevices;

      if (!devices.length) {
        devices =
          await loadCameraDevices();
      }

      if (!devices.length) {
        throw new Error(
          "No camera was found on this device."
        );
      }

      // ======================================================
      // SELECT CAMERA
      // ======================================================

      let cameraId =
        requestedCameraId ||
        selectedCameraId;

      const cameraExists =
        devices.some(
          (device) =>
            device.deviceId === cameraId
        );

      if (!cameraExists) {
        const preferred =
          findPreferredCamera(
            devices
          );

        cameraId =
          preferred?.deviceId ||
          devices[0]?.deviceId;
      }

      if (!cameraId) {
        throw new Error(
          "Unable to select a camera."
        );
      }

      if (mountedRef.current) {
        setSelectedCameraId(cameraId);
      }

      // ======================================================
      // CREATE ZXING READER
      // ======================================================

      const reader =
        new BrowserQRCodeReader();

      scannerReaderRef.current =
        reader;

      // ======================================================
      // START ZXING CAMERA
      // ======================================================

      const controls =
        await reader.decodeFromVideoDevice(
          cameraId,
          video,
          (result, error) => {

            // ----------------------------------------------
            // QR FOUND
            // ----------------------------------------------

            if (result) {

              const text =
                result.getText();

              if (text) {
                handleCustomerQR(text);
              }

            }

            // ----------------------------------------------
            // NORMAL ZXING SEARCH ERROR
            // ----------------------------------------------

            if (
              error &&
              error.name !==
                "NotFoundException"
            ) {
              console.debug(
                "ZXing scanner:",
                error
              );
            }

          }
        );

      scannerControlsRef.current =
        controls;

      // ======================================================
      // WAIT FOR VIDEO STREAM
      // ======================================================

      await new Promise((resolve) => {

        const checkVideo = () => {

          if (
            video.readyState >= 2 &&
            video.videoWidth > 0 &&
            video.videoHeight > 0
          ) {
            resolve();
            return;
          }

          window.setTimeout(
            checkVideo,
            100
          );
        };

        checkVideo();
      });

      // ======================================================
      // START PLAYBACK
      // ======================================================

      try {
        await video.play();
      } catch (playError) {

        console.debug(
          "Video playback warning:",
          playError
        );

      }

      // ======================================================
      // GET ACTIVE CAMERA TRACK
      // ======================================================

      const stream =
        video.srcObject;

      const track =
        stream?.getVideoTracks?.()[0];

      if (track) {

        const settings =
          track.getSettings?.();

        const capabilities =
          track.getCapabilities?.();

        console.log(
          "CAMERA SETTINGS:",
          settings
        );

        console.log(
          "CAMERA CAPABILITIES:",
          capabilities
        );

        // ==================================================
        // AUTOFOCUS
        //
        // IMPORTANT:
        // Apply AFTER video is already rendering.
        // ==================================================

        if (
          Array.isArray(
            capabilities?.focusMode
          )
        ) {

          console.log(
            "SUPPORTED FOCUS MODES:",
            capabilities.focusMode
          );

          // ----------------------------------------------
          // Continuous autofocus
          // ----------------------------------------------

          if (
            capabilities.focusMode.includes(
              "continuous"
            )
          ) {

            try {

              await track.applyConstraints({
                advanced: [
                  {
                    focusMode:
                      "continuous",
                  },
                ],
              });

              console.log(
                "CONTINUOUS AUTOFOCUS ENABLED"
              );

            } catch (focusError) {

              // IMPORTANT:
              // Do NOT stop the camera if autofocus
              // is unsupported or fails.

              console.debug(
                "Autofocus unavailable:",
                focusError
              );

            }

          }

        }

      }

      // ======================================================
      // MAKE SURE VIDEO IS STILL PLAYING
      // ======================================================

      if (
        video.paused ||
        video.readyState < 2
      ) {

        try {
          await video.play();
        } catch {}

      }

      // ======================================================
      // READY
      // ======================================================

      if (mountedRef.current) {

        setScanStatus(
          "ready"
        );

        setScannerLoading(
          false
        );

      }

    } catch (error) {

      console.error(
        "SCANNER START ERROR:",
        error
      );

      let message =
        "Unable to start the camera.";

      if (
        error?.name ===
        "NotAllowedError"
      ) {

        message =
          "Camera permission was denied.";

      } else if (
        error?.name ===
        "NotFoundError"
      ) {

        message =
          "No camera was found.";

      } else if (
        error?.name ===
        "NotReadableError"
      ) {

        message =
          "The camera is already being used by another application.";

      } else if (
        error?.name ===
        "OverconstrainedError"
      ) {

        message =
          "The selected camera settings are not supported by this device.";

      } else if (
        error?.message
      ) {

        message =
          error.message;

      }

      if (mountedRef.current) {

        setScannerError(
          message
        );

        setScanStatus(
          "error"
        );

      }

      playErrorSound();

      // ====================================================
      // CLEANUP
      // ====================================================

      try {
        scannerControlsRef.current?.stop();
      } catch {}

      scannerControlsRef.current =
        null;

    } finally {

      if (mountedRef.current) {

        setScannerLoading(
          false
        );

        setCameraSwitching(
          false
        );

      }

    }
  },
  [
    cameraDevices,
    findPreferredCamera,
    handleCustomerQR,
    loadCameraDevices,
    playErrorSound,
    selectedCameraId,
    stopScanner,
  ]
);

  // ==========================================================
  // OPEN SCANNER
  // ==========================================================

  const openScanner = useCallback(() => {
    // Stop anything that may still be running.
    stopScanner();

    setCustomer(null);
    setQrToken("");
    setPin("");
    setShowPin(false);

    setScannerError("");
    setScanStatus("ready");

    setScannerOpen(true);

    // --------------------------------------------------------
    // Modal needs to render the video element first.
    // --------------------------------------------------------

    startTimeoutRef.current =
      window.setTimeout(() => {
        if (mountedRef.current) {
          startScanner();
        }
      }, 300);
  }, [startScanner, stopScanner]);

  // ==========================================================
  // CLOSE SCANNER
  // ==========================================================

  const closeScanner = useCallback(() => {
    stopScanner();

    setScannerOpen(false);
    setScannerError("");
    setScanStatus("ready");
  }, [stopScanner]);

  // ==========================================================
  // CAMERA CHANGE
  // ==========================================================

  const handleCameraChange =
    useCallback(
      async (cameraId) => {
        if (!cameraId) {
          return;
        }

        if (
          cameraId === selectedCameraId
        ) {
          return;
        }

        setSelectedCameraId(cameraId);
        setCameraSwitching(true);
        setScannerError("");
        setScanStatus("processing");

        await startScanner(cameraId);
      },
      [
        selectedCameraId,
        startScanner,
      ]
    );

  // ==========================================================
  // CHECK CUSTOMER DETAILS
  // ==========================================================

  const checkCustomerDetails =
    useCallback(async () => {
      // ------------------------------------------------------
      // QR TOKEN
      // ------------------------------------------------------

      if (!qrToken) {
        playErrorSound();

        await Swal.fire({
          icon: "error",
          title: "QR Code Missing",
          text:
            "Please scan the customer's Royalty Card again.",
          confirmButtonColor: "#7c3aed",
        });

        return;
      }

      // ------------------------------------------------------
      // PIN
      // ------------------------------------------------------

      const cleanPin = pin.trim();

      if (!cleanPin) {
        playErrorSound();

        await Swal.fire({
          icon: "warning",
          title: "PIN Required",
          text:
            "Please enter the customer's PIN.",
          confirmButtonColor: "#7c3aed",
        });

        return;
      }

      if (
        cleanPin.length !== 4 ||
        !/^\d{4}$/.test(cleanPin)
      ) {
        playErrorSound();

        await Swal.fire({
          icon: "warning",
          title: "Invalid PIN",
          text:
            "The customer PIN must contain exactly 4 digits.",
          confirmButtonColor: "#7c3aed",
        });

        return;
      }

      setCheckingCustomer(true);

      try {
        const payload = {
          card_qr_token: qrToken,
          card_pin: cleanPin,
        };

        console.log(
          "=========================================="
        );

        console.log(
          "ROYALTY CARD VERIFICATION"
        );

        console.log(
          "ENDPOINT:",
          "/api/card/verify/"
        );

        console.log(
          "QR TOKEN:",
          qrToken
        );

        console.log(
          "VERIFYING CARD..."
        );

        console.log(
          "=========================================="
        );

        // ----------------------------------------------------
        // BACKEND REQUEST
        // ----------------------------------------------------

        const response =
          await api.post(
            "/api/card/verify/",
            payload
          );

        console.log(
          "CUSTOMER VERIFY RESPONSE:",
          response.data
        );

        // ----------------------------------------------------
        // Extract card.
        // ----------------------------------------------------

        const customerData =
          response.data?.card;

        if (
          !customerData ||
          !customerData.id
        ) {
          console.error(
            "INVALID CUSTOMER RESPONSE:",
            response.data
          );

          throw new Error(
            "The server verified the card but did not return valid customer information."
          );
        }

        // ----------------------------------------------------
        // Save customer.
        // ----------------------------------------------------

        setCustomer(customerData);

        // ----------------------------------------------------
        // Close PIN modal.
        // ----------------------------------------------------

        setPinModalOpen(false);

        setPin("");
        setShowPin(false);

        // ----------------------------------------------------
        // Success sound.
        // ----------------------------------------------------

        playSuccessSound();

        // ----------------------------------------------------
        // Success message.
        // ----------------------------------------------------

        await Swal.fire({
          icon: "success",
          title: "Customer Verified",
          text: `${
            customerData.full_name ||
            "Customer"
          } has been verified successfully.`,
          timer: 1400,
          showConfirmButton: false,
          allowOutsideClick: false,
        });
      } catch (error) {
        console.error(
          "CUSTOMER VERIFY ERROR:",
          error.response?.data ||
            error
        );

        playErrorSound();

        const backendData =
          error.response?.data;

        let message =
          "Unable to verify this customer.";

        // ----------------------------------------------------
        // Generic backend message.
        // ----------------------------------------------------

        if (
          backendData?.error
        ) {
          message =
            backendData.error;
        } else if (
          backendData?.message
        ) {
          message =
            backendData.message;
        } else if (
          backendData?.detail
        ) {
          message =
            backendData.detail;
        } else if (
          error?.message
        ) {
          message = error.message;
        }

        // ----------------------------------------------------
        // DRF field errors.
        // ----------------------------------------------------

        if (
          typeof backendData ===
            "object" &&
          backendData !== null
        ) {
          if (
            backendData.card_qr_token
          ) {
            message =
              Array.isArray(
                backendData.card_qr_token
              )
                ? backendData.card_qr_token.join(
                    " "
                  )
                : String(
                    backendData.card_qr_token
                  );
          }

          if (
            backendData.card_pin
          ) {
            message =
              Array.isArray(
                backendData.card_pin
              )
                ? backendData.card_pin.join(
                    " "
                  )
                : String(
                    backendData.card_pin
                  );
          }
        }

        // ----------------------------------------------------
        // HTTP 401.
        // ----------------------------------------------------

        if (
          error.response?.status ===
          401
        ) {
          message =
            backendData?.error ||
            backendData?.message ||
            "Invalid customer PIN.";
        }

        // ----------------------------------------------------
        // HTTP 404.
        // ----------------------------------------------------

        if (
          error.response?.status ===
          404
        ) {
          message =
            backendData?.error ||
            backendData?.message ||
            "Invalid Royalty Card.";
        }

        // ----------------------------------------------------
        // HTTP 400.
        // ----------------------------------------------------

        if (
          error.response?.status ===
          400
        ) {
          message =
            backendData?.error ||
            backendData?.message ||
            message;
        }

        // ----------------------------------------------------
        // HTTP 403.
        // ----------------------------------------------------

        if (
          error.response?.status ===
          403
        ) {
          message =
            backendData?.error ||
            backendData?.message ||
            "You are not authorized to verify this card.";
        }

        await Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: message,
          confirmButtonText:
            "Try Again",
          confirmButtonColor:
            "#7c3aed",
        });
      } finally {
        if (mountedRef.current) {
          setCheckingCustomer(false);
        }
      }
    }, [
      pin,
      playErrorSound,
      playSuccessSound,
      qrToken,
    ]);

  // ==========================================================
  // BACK TO PIN
  // ==========================================================

  const backToPin = useCallback(() => {
    setPin("");
    setShowPin(false);
    setPinModalOpen(true);
  }, []);

  // ==========================================================
  // SCAN ANOTHER CUSTOMER
  // ==========================================================

  const scanAnotherCustomer =
    useCallback(() => {
      stopScanner();

      setCustomer(null);
      setQrToken("");
      setPin("");
      setShowPin(false);
      setScannerError("");
      setScanStatus("ready");

      setPinModalOpen(false);

      // Open scanner after state update.
      window.setTimeout(() => {
        if (mountedRef.current) {
          openScanner();
        }
      }, 100);
    }, [openScanner, stopScanner]);

  // ==========================================================
  // CLEANUP
  // ==========================================================

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;

      if (startTimeoutRef.current) {
        window.clearTimeout(
          startTimeoutRef.current
        );
      }

      stopScanner();
    };
  }, [stopScanner]);

  // ==========================================================
  // CAMERA OPTIONS
  // ==========================================================

  const cameraOptions =
    cameraDevices.map(
      (device, index) => ({
        value: device.deviceId,
        label:
          device.label ||
          `Camera ${index + 1}`,
      })
    );

  // ==========================================================
  // SCANNER STATUS
  // ==========================================================

  const scannerStatus = {
    ready: {
      label:
        "READY TO SCAN CUSTOMER CARD",
      icon: <ScanOutlined />,
    },

    processing: {
      label:
        "STARTING CAMERA...",
      icon: <SyncOutlined spin />,
    },

    success: {
      label:
        "QR CODE DETECTED",
      icon: (
        <CheckCircleOutlined />
      ),
    },

    error: {
      label:
        "SCANNER ERROR",
      icon: (
        <WarningOutlined />
      ),
    },
  };

  // ==========================================================
  // CUSTOMER LEVEL COLORS
  // ==========================================================

  const levelColor = {
    NEW: "default",
    STARTER: "cyan",
    BRONZE: "orange",
    BRONZE_PLUS: "gold",
    SILVER: "default",
    GOLD: "gold",
    VIP: "magenta",
    PLATINUM: "purple",
    ELITE: "blue",
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <>
      <style>{`
        * {
          box-sizing: border-box;
        }

        /* =====================================================
           PAGE
        ===================================================== */

        .royalty-atm-page {
          min-height: 100vh;
          padding: 20px;

          background:
            radial-gradient(
              circle at top right,
              rgba(124, 58, 237, 0.09),
              transparent 32%
            ),
            radial-gradient(
              circle at bottom left,
              rgba(37, 99, 235, 0.07),
              transparent 30%
            ),
            #f5f7fb;
        }

        /* =====================================================
           HEADER
        ===================================================== */

        .royalty-atm-header {
          min-height: 105px;

          border-radius: 22px;

          padding: 20px 24px;

          display: flex;

          align-items: center;

          justify-content: space-between;

          gap: 20px;

          color: white;

          background:
            linear-gradient(
              135deg,
              #111827,
              #312e81,
              #4c1d95
            );

          box-shadow:
            0 18px 45px
            rgba(15, 23, 42, 0.18);

          margin-bottom: 20px;
        }

        .atm-header-left {
          display: flex;

          align-items: center;

          gap: 15px;
        }

        .atm-header-icon {
          width: 58px;
          height: 58px;

          min-width: 58px;

          border-radius: 17px;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 27px;

          background:
            linear-gradient(
              135deg,
              #8b5cf6,
              #2563eb
            );

          box-shadow:
            0 10px 30px
            rgba(124, 58, 237, 0.35);
        }

        .atm-header-title {
          color: white !important;

          margin: 0 !important;
        }

        .atm-header-subtitle {
          color:
            rgba(255, 255, 255, 0.65);

          margin-top: 4px;

          display: block;
        }

        /* =====================================================
           MAIN BUTTON
        ===================================================== */

        .atm-scan-main-btn {
          min-height: 50px;

          border: 0;

          border-radius: 13px;

          padding: 0 20px;

          display: flex;

          align-items: center;

          justify-content: center;

          gap: 9px;

          color: white;

          font-weight: 800;

          cursor: pointer;

          background:
            linear-gradient(
              135deg,
              #7c3aed,
              #2563eb
            );

          box-shadow:
            0 10px 25px
            rgba(79, 70, 229, 0.3);

          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }

        .atm-scan-main-btn:hover {
          transform: translateY(-2px);

          box-shadow:
            0 14px 30px
            rgba(79, 70, 229, 0.4);
        }

        /* =====================================================
           EMPTY CARD
        ===================================================== */

        .atm-empty-card {
          min-height: 420px;

          border-radius: 22px;

          background: white;

          box-shadow:
            0 10px 35px
            rgba(15, 23, 42, 0.07);

          display: flex;

          align-items: center;

          justify-content: center;

          flex-direction: column;

          text-align: center;

          padding: 35px;
        }

        .atm-empty-icon {
          width: 90px;
          height: 90px;

          border-radius: 27px;

          background: #f3e8ff;

          color: #7c3aed;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 38px;

          margin-bottom: 18px;
        }

        .atm-empty-title {
          font-size: 22px;

          font-weight: 800;

          color: #111827;
        }

        .atm-empty-text {
          max-width: 500px;

          color: #64748b;

          line-height: 1.7;

          margin-bottom: 20px;
        }

        /* =====================================================
           CUSTOMER CARD
        ===================================================== */

        .customer-card {
          background: white;

          border-radius: 22px;

          overflow: hidden;

          box-shadow:
            0 12px 40px
            rgba(15, 23, 42, 0.08);

          animation:
            customerAppear
            0.35s ease;
        }

        @keyframes customerAppear {
          from {
            opacity: 0;

            transform:
              translateY(12px);
          }

          to {
            opacity: 1;

            transform:
              translateY(0);
          }
        }

        .customer-card-header {
          padding: 24px;

          color: white;

          background:
            linear-gradient(
              135deg,
              #111827,
              #312e81,
              #4c1d95
            );

          display: flex;

          justify-content:
            space-between;

          align-items: center;

          gap: 15px;
        }

        .customer-profile {
          display: flex;

          align-items: center;

          gap: 15px;

          min-width: 0;
        }

        .customer-avatar {
          width: 66px;
          height: 66px;

          min-width: 66px;

          border-radius: 20px;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 28px;

          background:
            rgba(255, 255, 255, 0.12);

          border:
            1px solid
            rgba(255, 255, 255, 0.15);
        }

        .customer-name {
          color: white !important;

          margin: 0 !important;

          font-size: 23px !important;
        }

        .customer-contact {
          color:
            rgba(255, 255, 255, 0.64);

          margin-top: 4px;

          display: block;
        }

        .customer-level {
          font-weight: 800;

          letter-spacing: 0.4px;
        }

        /* =====================================================
           CUSTOMER GRID
        ===================================================== */

        /* =====================================================
   ROYALTY BANK-STYLE BALANCE
===================================================== */

.royalty-balance-card {
  margin: 22px;
  padding: 28px 30px;

  border-radius: 22px;

  color: white;

  background:
    radial-gradient(
      circle at top right,
      rgba(255, 255, 255, 0.16),
      transparent 30%
    ),
    linear-gradient(
      135deg,
      #111827,
      #312e81,
      #4c1d95
    );

  box-shadow:
    0 18px 45px
    rgba(49, 46, 129, 0.22);

  position: relative;

  overflow: hidden;
}

.royalty-balance-card::before {
  content: "";

  position: absolute;

  width: 220px;
  height: 220px;

  right: -100px;
  bottom: -130px;

  border-radius: 50%;

  background:
    rgba(255, 255, 255, 0.07);

  pointer-events: none;
}

.royalty-balance-top {
  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 15px;

  position: relative;

  z-index: 2;
}

.royalty-balance-label {
  display: block;

  color:
    rgba(255, 255, 255, 0.72);

  font-size: 12px;

  font-weight: 800;

  letter-spacing: 1.2px;

  text-transform: uppercase;
}

.royalty-balance-subtitle {
  display: block;

  color:
    rgba(255, 255, 255, 0.48);

  font-size: 12px;

  margin-top: 5px;
}

.royalty-balance-icon {
  width: 48px;
  height: 48px;

  border-radius: 15px;

  display: flex;

  align-items: center;
  justify-content: center;

  font-size: 23px;

  font-weight: 900;

  color: white;

  background:
    rgba(255, 255, 255, 0.12);

  border:
    1px solid
    rgba(255, 255, 255, 0.15);

  flex-shrink: 0;
}

.royalty-balance-amount {
  position: relative;

  z-index: 2;

  margin-top: 12px;

  font-size: clamp(
    46px,
    7vw,
    78px
  );

  line-height: 1;

  font-weight: 900;

  letter-spacing: -2px;

  color: white;

  text-shadow:
    0 4px 20px
    rgba(0, 0, 0, 0.18);

  word-break: break-word;
}

.royalty-balance-reward {
  position: relative;

  z-index: 2;

  display: flex;

  align-items: center;

  flex-wrap: wrap;

  gap: 8px;

  margin-top: 18px;

  color:
    rgba(255, 255, 255, 0.72);

  font-size: 13px;

  font-weight: 700;
}

.royalty-balance-reward span:last-child {
  padding-left: 5px;

  color:
    rgba(255, 255, 255, 0.45);
}

        .customer-grid {
          padding: 22px;

          display: grid;

          grid-template-columns:
            repeat(4, minmax(0, 1fr));

          gap: 13px;
        }

        .customer-stat {
          padding: 17px;

          border-radius: 15px;

          background: #f8fafc;

          border:
            1px solid
            #eef2f7;

          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease;
        }

        .customer-stat:hover {
          transform:
            translateY(-2px);

          box-shadow:
            0 8px 22px
            rgba(15, 23, 42, 0.06);
        }

        .customer-stat-icon {
          width: 37px;
          height: 37px;

          border-radius: 10px;

          display: flex;

          align-items: center;

          justify-content: center;

          background: #eef2ff;

          color: #4f46e5;

          margin-bottom: 11px;
        }

        .customer-stat-label {
          display: block;

          font-size: 11px;

          color: #64748b;

          text-transform:
            uppercase;

          letter-spacing: 0.4px;

          font-weight: 700;
        }

        .customer-stat-value {
          display: block;

          color: #111827;

          font-size: 21px;

          font-weight: 850;

          margin-top: 4px;

          overflow-wrap:
            anywhere;
        }

        /* =====================================================
           REWARD
        ===================================================== */

        .reward-box {
          margin:
            0 22px 22px;

          padding: 20px;

          border-radius: 17px;

          background:
            linear-gradient(
              135deg,
              #f5f3ff,
              #eff6ff
            );

          border:
            1px solid
            #e0e7ff;

          display: flex;

          align-items: center;

          justify-content:
            space-between;

          gap: 20px;
        }

        .reward-left {
          display: flex;

          align-items: center;

          gap: 13px;
        }

        .reward-icon {
          width: 49px;
          height: 49px;

          border-radius: 14px;

          background: #7c3aed;

          color: white;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 21px;
        }

        .reward-name {
          display: block;

          color: #312e81;

          font-weight: 850;
        }

        .reward-points {
          display: block;

          color: #64748b;

          font-size: 12px;

          margin-top: 3px;
        }

        .reward-amount {
          color: #4f46e5;

          font-size: 27px;

          font-weight: 900;
        }

        /* =====================================================
           FOOTER
        ===================================================== */

        .customer-footer {
          padding:
            16px 22px;

          border-top:
            1px solid
            #eef2f7;

          display: flex;

          justify-content:
            space-between;

          flex-wrap: wrap;

          gap: 10px;

          color: #64748b;

          font-size: 12px;
        }

        /* =====================================================
           SCANNER MODAL
        ===================================================== */

        .scanner-modal
        .ant-modal-content {
          padding: 0 !important;

          overflow: hidden;

          border-radius: 23px;

          background: #060b16;
        }

        .scanner-modal
        .ant-modal-body {
          padding: 0 !important;
        }

        .scanner-container {
          padding: 17px;

          color: white;

          background:
            radial-gradient(
              circle at top,
              rgba(124, 58, 237, 0.18),
              transparent 42%
            ),
            #060b16;
        }

        .scanner-header {
          display: flex;

          justify-content:
            space-between;

          align-items: center;

          gap: 12px;

          margin-bottom: 13px;
        }

        .scanner-header-title {
          color: white !important;

          margin: 0 !important;
        }

        .scanner-header-subtitle {
          display: block;

          color:
            rgba(255, 255, 255, 0.55);

          font-size: 12px;

          margin-top: 3px;
        }

        .scanner-close-btn {
          width: 39px;
          height: 39px;

          border-radius: 10px;

          border:
            1px solid
            rgba(255, 255, 255, 0.12);

          background:
            rgba(255, 255, 255, 0.06);

          color: white;

          cursor: pointer;

          display: flex;

          align-items: center;

          justify-content: center;
        }

        .scanner-close-btn:hover {
          background:
            rgba(255, 255, 255, 0.12);
        }

        /* =====================================================
           CAMERA CONTROL
        ===================================================== */

        .camera-control-bar {
          display: flex;

          align-items: center;

          gap: 9px;

          margin-bottom: 12px;
        }

        .camera-control-label {
          font-size: 12px;

          color:
            rgba(255, 255, 255, 0.65);

          white-space: nowrap;
        }

        .scanner-camera-select {
          flex: 1;
        }

        .scanner-camera-select
        .ant-select-selector {
          background:
            rgba(255, 255, 255, 0.07)
            !important;

          border:
            1px solid
            rgba(255, 255, 255, 0.12)
            !important;

          border-radius:
            10px !important;

          color: white !important;

          min-height:
            40px !important;
        }

        .scanner-camera-select
        .ant-select-selection-item {
          color: white !important;
        }

        .scanner-camera-select
        .ant-select-selection-placeholder {
          color:
            rgba(255, 255, 255, 0.55) !important;
        }

        /* =====================================================
           CAMERA
        ===================================================== */


.scanner-camera-wrapper {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #000;
  border-radius: 18px;
  aspect-ratio: 4 / 3;
}

.scanner-video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #000;
}

        .scanner-video {
          position: absolute;

          inset: 0;

          display: block;

          width: 100%;

          height: 100%;

          object-fit: cover;

          background: black;
        }

        .scanner-camera-wrapper::after {
          content: "";

          position: absolute;

          inset: 0;

          pointer-events: none;

          background:
            rgba(0, 0, 0, 0.25);
        }

        /* =====================================================
           SCANNER FRAME
        ===================================================== */

        .scanner-frame {
          position: absolute;

          z-index: 5;

          width:
            min(72%, 320px);

          aspect-ratio: 1;

          left: 50%;

          top: 50%;

          transform:
            translate(-50%, -50%);

          pointer-events: none;
        }

        .scanner-line {
          position: absolute;

          z-index: 7;

          left: 4%;

          right: 4%;

          top: 4%;

          height: 2px;

          border-radius: 50px;

          background:
            linear-gradient(
              90deg,
              transparent,
              #a78bfa 20%,
              white 50%,
              #60a5fa 80%,
              transparent
            );

          box-shadow:
            0 0 7px #a78bfa,
            0 0 18px #7c3aed;

          animation:
            royaltyLaser
            2s ease-in-out infinite;
        }

        @keyframes royaltyLaser {
          0% {
            top: 5%;
            opacity: 0.4;
          }

          50% {
            opacity: 1;
          }

          100% {
            top: 95%;
            opacity: 0.4;
          }
        }

        .scanner-corner {
          position: absolute;

          z-index: 8;

          width: 39px;

          height: 39px;

          border-color:
            #a78bfa;

          border-style: solid;

          filter:
            drop-shadow(
              0 0 6px
              rgba(167, 139, 250, 0.8)
            );
        }

        .scanner-corner.tl {
          top: 0;
          left: 0;

          border-width:
            4px 0 0 4px;

          border-radius:
            8px 0 0 0;
        }

        .scanner-corner.tr {
          top: 0;
          right: 0;

          border-width:
            4px 4px 0 0;

          border-radius:
            0 8px 0 0;
        }

        .scanner-corner.bl {
          bottom: 0;
          left: 0;

          border-width:
            0 0 4px 4px;

          border-radius:
            0 0 0 8px;
        }

        .scanner-corner.br {
          bottom: 0;
          right: 0;

          border-width:
            0 4px 4px 0;

          border-radius:
            0 0 8px 0;
        }

        /* =====================================================
           LOADING
        ===================================================== */

        .scanner-loading {
          position: absolute;

          z-index: 20;

          inset: 0;

          display: flex;

          align-items: center;

          justify-content: center;

          flex-direction: column;

          gap: 11px;

          background:
            rgba(3, 7, 18, 0.72);

          backdrop-filter:
            blur(4px);
        }

        .scanner-loading-text {
          color: white;

          font-size: 13px;

          font-weight: 800;
        }

        /* =====================================================
           STATUS
        ===================================================== */

        .scanner-status {
          min-height: 43px;

          margin-top: 12px;

          border-radius: 11px;

          padding: 8px 12px;

          display: flex;

          justify-content: center;

          align-items: center;

          gap: 8px;

          color: #c4b5fd;

          background:
            rgba(255, 255, 255, 0.05);

          border:
            1px solid
            rgba(255, 255, 255, 0.08);

          font-size: 12px;

          font-weight: 800;

          letter-spacing: 0.4px;
        }

        .scanner-status.success {
          color: #86efac;

          background:
            rgba(22, 163, 74, 0.13);
        }

        .scanner-status.error {
          color: #fca5a5;

          background:
            rgba(220, 38, 38, 0.13);
        }

        /* =====================================================
           ERROR
        ===================================================== */

        .scanner-error {
          margin-top: 10px;

          padding: 11px;

          border-radius: 11px;

          color: #fecaca;

          background:
            rgba(220, 38, 38, 0.12);

          border:
            1px solid
            rgba(248, 113, 113, 0.25);

          font-size: 12px;

          line-height: 1.6;
        }

        .scanner-retry {
          margin-top: 9px;

          border: 0;

          border-radius: 8px;

          padding: 8px 12px;

          background:
            rgba(255, 255, 255, 0.1);

          color: white;

          cursor: pointer;

          font-weight: 700;
        }

        .scanner-retry:hover {
          background:
            rgba(255, 255, 255, 0.16);
        }

        /* =====================================================
           PIN MODAL
        ===================================================== */

        .pin-modal
        .ant-modal-content {
          border-radius: 21px;

          overflow: hidden;
        }

        .pin-container {
          text-align: center;

          padding: 8px 3px;
        }

        .pin-icon {
          width: 70px;
          height: 70px;

          border-radius: 21px;

          margin:
            0 auto 15px;

          display: flex;

          align-items: center;

          justify-content: center;

          font-size: 28px;

          color: #7c3aed;

          background: #f3e8ff;
        }

        .pin-title {
          margin: 0 !important;

          color: #111827 !important;
        }

        .pin-subtitle {
          display: block;

          color: #64748b;

          margin-top: 5px;

          margin-bottom: 18px;

          line-height: 1.6;
        }

        .pin-input-wrapper {
          position: relative;
        }

        .pin-input {
          height: 58px !important;

          border-radius:
            13px !important;

          font-size:
            25px !important;

          letter-spacing: 8px;

          text-align: center;

          padding-right:
            48px !important;
        }

        .pin-eye {
          position: absolute;

          right: 14px;

          top: 50%;

          transform:
            translateY(-50%);

          border: 0;

          background:
            transparent;

          color: #64748b;

          cursor: pointer;

          font-size: 17px;
        }

        .pin-eye:hover {
          color: #7c3aed;
        }

        .check-customer-btn {
          width: 100%;

          min-height: 52px;

          margin-top: 13px;

          border: 0;

          border-radius: 12px;

          background:
            linear-gradient(
              135deg,
              #7c3aed,
              #2563eb
            );

          color: white;

          font-weight: 850;

          cursor: pointer;

          box-shadow:
            0 9px 25px
            rgba(79, 70, 229, 0.23);

          transition:
            transform 0.2s ease,
            opacity 0.2s ease;
        }

        .check-customer-btn:hover:not(:disabled) {
          transform:
            translateY(-1px);
        }

        .check-customer-btn:disabled {
          cursor:
            not-allowed;

          opacity: 0.65;
        }

        .pin-security-note {
          margin-top: 13px;

          padding: 9px;

          border-radius: 9px;

          background: #f8fafc;

          color: #64748b;

          font-size: 11px;

          line-height: 1.5;
        }

        /* =====================================================
           CUSTOMER ACTIONS
        ===================================================== */

        .customer-actions {
          padding:
            0 22px 22px;

          display: flex;

          gap: 10px;

          flex-wrap: wrap;
        }

        /* =====================================================
           RESPONSIVE
        ===================================================== */

        @media (max-width: 900px) {
          .customer-grid {
            grid-template-columns:
              repeat(2, minmax(0, 1fr));
          }

          .royalty-atm-header {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .atm-scan-main-btn {
            width: 100%;
          }
        }

        @media (max-width: 600px) {
          .royalty-atm-page {
            padding: 12px;
          }

          .royalty-atm-header {
            padding: 17px;

            border-radius: 17px;
          }

          .atm-header-title {
            font-size:
              20px !important;
          }

          .atm-header-icon {
            width: 49px;

            height: 49px;

            min-width: 49px;

            font-size: 22px;
          }

          .atm-header-subtitle {
            font-size: 12px;

            line-height: 1.5;
          }

          .customer-card-header {
            padding: 18px;

            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .customer-grid {
            padding: 15px;

            grid-template-columns:
              repeat(2, minmax(0, 1fr));
          }

          .customer-stat {
            padding: 13px;
          }

          .customer-stat-value {
            font-size: 18px;
          }

          .reward-box {
            margin:
              0 15px 15px;

            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .scanner-container {
            padding: 12px;
          }

          .scanner-camera-wrapper {
            aspect-ratio: 3 / 4;
          }

          .scanner-frame {
            width:
              min(76%, 285px);
          }

          .camera-control-bar {
            align-items:
              flex-start;

            flex-direction:
              column;
          }

          .scanner-camera-select {
            width: 100%;
          }

          .customer-footer {
            padding:
              15px;
          }

          .customer-actions {
            padding:
              0 15px 15px;

            flex-direction:
              column;
          }

          .customer-actions .ant-btn {
            width: 100%;
          }
        }
      `}</style>

      {/* ======================================================
          MAIN PAGE
      ====================================================== */}

      <div className="royalty-atm-page">

        {/* ====================================================
            HEADER
        ==================================================== */}

        <div className="royalty-atm-header">

          <div className="atm-header-left">

            <div className="atm-header-icon">
              <QrcodeOutlined />
            </div>

            <div>

              <Title
                level={3}
                className="atm-header-title"
              >
                Royalty ATM Scanner
              </Title>

              <span className="atm-header-subtitle">
                Scan a customer's Royalty Card
                to securely view their account.
              </span>

            </div>

          </div>

          <button
            className="atm-scan-main-btn"
            onClick={openScanner}
            type="button"
          >
            <CameraOutlined />

            Scan Royalty Card
          </button>

        </div>

        {/* ====================================================
            CUSTOMER AREA
        ==================================================== */}

        {!customer ? (

          <div className="atm-empty-card">

            <div className="atm-empty-icon">
              <QrcodeOutlined />
            </div>

            <div className="atm-empty-title">
              No Customer Selected
            </div>

            <div className="atm-empty-text">
              Scan the customer's Royalty Card
              QR code. After scanning, you will
              be asked for the customer's PIN
              before their account details are
              displayed.
            </div>

            <button
              className="atm-scan-main-btn"
              onClick={openScanner}
              type="button"
            >
              <ScanOutlined />

              Start Customer Scan
            </button>

          </div>

        ) : (

          <div className="customer-card">

            {/* ==================================================
                CUSTOMER HEADER
            ================================================== */}

            <div className="customer-card-header">

              <div className="customer-profile">

                <div className="customer-avatar">
                  <UserOutlined />
                </div>

                <div>

                  <Title
                    level={3}
                    className="customer-name"
                  >
                    {customer.full_name ||
                      "Unknown Customer"}
                  </Title>

                  <span className="customer-contact">
                    <PhoneOutlined />

                    {" "}

                    {customer.phone ||
                      "No phone"}
                  </span>

                </div>

              </div>

              <Tag
                color={
                  levelColor[
                    customer.level
                  ] || "blue"
                }
                className="customer-level"
              >
                {customer.level ||
                  "CUSTOMER"}
              </Tag>

            </div>

            {/* ==================================================
                CUSTOMER GRID
            ================================================== */}

            {/* =======================================================
    ROYALTY BALANCE
======================================================= */}

<div className="royalty-balance-card">

  <div className="royalty-balance-top">

    <div>
      <span className="royalty-balance-label">
        AVAILABLE REWARD BALANCE
      </span>

      <span className="royalty-balance-subtitle">
        Your current Royalty reward value
      </span>
    </div>

    <div className="royalty-balance-icon">
      $
    </div>

  </div>

  <div className="royalty-balance-amount">
    {formatMoney(
      getCustomerRewardBalance(customer)
    )}
  </div>

  {customer?.rewards_available && (
    <div className="royalty-balance-reward">

      <GiftOutlined />

      <span>
        {customer.rewards_available.name}
      </span>

      <span>
        {formatNumber(
          customer.rewards_available.points
        )} points
      </span>

    </div>
  )}

</div>

            <div className="customer-grid">

              {/* TOTAL POINTS */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <TrophyOutlined />
                </div>

                <span className="customer-stat-label">
                  Total Points
                </span>

                <span className="customer-stat-value">
                  {formatNumber(
                    customer.total_points
                  )}
                </span>

              </div>

              {/* CYCLE POINTS */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <GiftOutlined />
                </div>

                <span className="customer-stat-label">
                  Cycle Points
                </span>

                <span className="customer-stat-value">
                  {formatNumber(
                    customer.reward_cycle_points
                  )}
                </span>

              </div>

              {/* PURCHASES */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <ShoppingOutlined />
                </div>

                <span className="customer-stat-label">
                  Purchases
                </span>

                <span className="customer-stat-value">
                  {formatNumber(
                    customer.total_purchases
                  )}
                </span>

              </div>

              {/* REDEMPTIONS */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <HistoryOutlined />
                </div>

                <span className="customer-stat-label">
                  Redemptions
                </span>

                <span className="customer-stat-value">
                  {formatNumber(
                    customer.total_redemptions
                  )}
                </span>

              </div>

              {/* CYCLE COMPLETED */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <CrownOutlined />
                </div>

                <span className="customer-stat-label">
                  Cycle Completed
                </span>

                <span className="customer-stat-value">
                  {formatNumber(
                    customer.cycle_completed_count
                  )}
                </span>

              </div>

              {/* EXPIRY */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <CalendarOutlined />
                </div>

                <span className="customer-stat-label">
                  Card Expiry
                </span>

                <span className="customer-stat-value">
                  {formatDate(
                    customer.expiry_date
                  )}
                </span>

              </div>

              {/* EMAIL */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <MailOutlined />
                </div>

                <span className="customer-stat-label">
                  Email
                </span>

                <span
                  className="customer-stat-value"
                  style={{
                    fontSize: 14,
                  }}
                >
                  {customer.email ||
                    "—"}
                </span>

              </div>

              {/* LAST ACTIVITY */}

              <div className="customer-stat">

                <div className="customer-stat-icon">
                  <HistoryOutlined />
                </div>

                <span className="customer-stat-label">
                  Last Activity
                </span>

                <span
                  className="customer-stat-value"
                  style={{
                    fontSize: 13,
                  }}
                >
                  {formatDateTime(
                    customer.last_activity
                  )}
                </span>

              </div>

            </div>

            {/* ==================================================
                REWARD
            ================================================== */}

            {customer.rewards_available ? (

              <div className="reward-box">

                <div className="reward-left">

                  <div className="reward-icon">
                    <GiftOutlined />
                  </div>

                  <div>

                    <span className="reward-name">
                      {
                        customer
                          .rewards_available
                          ?.name ||
                        "Reward Available"
                      }
                    </span>

                    <span className="reward-points">
                      Requires{" "}
                      {formatNumber(
                        customer
                          .rewards_available
                          ?.points
                      )}{" "}
                      points
                    </span>

                  </div>

                </div>

                <div className="reward-amount">
                  {formatMoney(
                    customer
                      .rewards_available
                      ?.amount
                  )}
                </div>

              </div>

            ) : (

              <div className="reward-box">

                <div className="reward-left">

                  <div className="reward-icon">
                    <GiftOutlined />
                  </div>

                  <div>

                    <span className="reward-name">
                      No Reward Available
                    </span>

                    <span className="reward-points">
                      Customer has no
                      redeemable reward
                      at this time.
                    </span>

                  </div>

                </div>

              </div>

            )}

            {/* ==================================================
                FOOTER
            ================================================== */}

            <div className="customer-footer">

              <span>
                <SafetyOutlined />

                {" "}

                Customer verified securely
              </span>

              <span>
                Card ID: #{customer.id}
              </span>

            </div>

            {/* ==================================================
                ACTIONS
            ================================================== */}

            <div className="customer-actions">

              <Button
                type="primary"
                icon={<ScanOutlined />}
                onClick={
                  scanAnotherCustomer
                }
              >
                Scan Another Customer
              </Button>

              <Button
                icon={<SafetyOutlined />}
                onClick={backToPin}
              >
                Verify Again
              </Button>

            </div>

          </div>

        )}

      </div>

      {/* ======================================================
          QR SCANNER MODAL
      ====================================================== */}

      <Modal
        open={scannerOpen}
        onCancel={closeScanner}
        footer={null}
        width={560}
        centered
        closable={false}
        className="scanner-modal"
        destroyOnClose
      >

        <div className="scanner-container">

          {/* ==================================================
              SCANNER HEADER
          ================================================== */}

          <div className="scanner-header">

            <div>

              <Title
                level={3}
                className="scanner-header-title"
              >
                Scan Royalty Card
              </Title>

              <span className="scanner-header-subtitle">
                Position the customer's QR
                code inside the scanning frame.
              </span>

            </div>

            <button
              className="scanner-close-btn"
              onClick={closeScanner}
              type="button"
            >
              <CloseOutlined />
            </button>

          </div>

          {/* ==================================================
              CAMERA SELECTOR
          ================================================== */}

          {cameraDevices.length > 1 && (

            <div className="camera-control-bar">

              <span className="camera-control-label">
                Camera
              </span>

              <Select
                value={
                  selectedCameraId || undefined
                }
                options={cameraOptions}
                onChange={
                  handleCameraChange
                }
                loading={
                  cameraSwitching
                }
                className="scanner-camera-select"
                suffixIcon={
                  <CameraOutlined
                    style={{
                      color:
                        "white",
                    }}
                  />
                }
                dropdownStyle={{
                  minWidth: 250,
                }}
                placeholder="Select camera"
              />

            </div>

          )}

          {/* ==================================================
              CAMERA
          ================================================== */}

          <div className="scanner-camera-wrapper">

<video
  ref={videoRef}
  className="scanner-video"
  muted
  playsInline
  autoPlay
  controls={false}
/>

            {/* SCANNING FRAME */}

            <div className="scanner-frame">

              <div className="scanner-line" />

              <div className="scanner-corner tl" />

              <div className="scanner-corner tr" />

              <div className="scanner-corner bl" />

              <div className="scanner-corner br" />

            </div>

            {/* LOADING */}

            {scannerLoading && (

              <div className="scanner-loading">

                <Spin size="large" />

                <span className="scanner-loading-text">
                  Starting camera...
                </span>

              </div>

            )}

          </div>

          {/* ==================================================
              STATUS
          ================================================== */}

          <div
            className={`scanner-status ${
              scanStatus ===
              "success"
                ? "success"
                : scanStatus ===
                  "error"
                ? "error"
                : ""
            }`}
          >

            {
              scannerStatus[
                scanStatus
              ]?.icon
            }

            {
              scannerStatus[
                scanStatus
              ]?.label
            }

          </div>

          {/* ==================================================
              ERROR
          ================================================== */}

          {scannerError && (

            <div className="scanner-error">

              <WarningOutlined />

              {" "}

              {scannerError}

              <br />

              <button
                className="scanner-retry"
                onClick={() =>
                  startScanner()
                }
                type="button"
              >
                <ReloadOutlined />

                {" "}

                Retry Camera
              </button>

            </div>

          )}

        </div>

      </Modal>

      {/* ======================================================
          PIN MODAL
      ====================================================== */}

      <Modal
        open={pinModalOpen}
        onCancel={() => {
          setPinModalOpen(false);

          setPin("");

          setShowPin(false);
        }}
        footer={null}
        width={410}
        centered
        className="pin-modal"
        destroyOnClose
      >

        <div className="pin-container">

          <div className="pin-icon">
            <SafetyOutlined />
          </div>

          <Title
            level={3}
            className="pin-title"
          >
            Customer PIN Required
          </Title>

          <Text className="pin-subtitle">
            QR code detected successfully.
            Enter the customer's 4-digit PIN
            to securely view their details.
          </Text>

          {/* ==================================================
              PIN INPUT
          ================================================== */}

          <div className="pin-input-wrapper">

            <Input
              className="pin-input"
              type={
                showPin
                  ? "text"
                  : "password"
              }
              maxLength={4}
              value={pin}
              onChange={(event) => {
                const value =
                  event.target.value.replace(
                    /\D/g,
                    ""
                  );

                setPin(value);
              }}
              onPressEnter={
                checkCustomerDetails
              }
              placeholder="••••"
              autoFocus
              inputMode="numeric"
              autoComplete="off"
            />

            <button
              className="pin-eye"
              type="button"
              onClick={() =>
                setShowPin(
                  (previous) =>
                    !previous
                )
              }
              aria-label={
                showPin
                  ? "Hide PIN"
                  : "Show PIN"
              }
            >
              {showPin ? (
                <EyeInvisibleOutlined />
              ) : (
                <EyeOutlined />
              )}
            </button>

          </div>

          {/* ==================================================
              VERIFY BUTTON
          ================================================== */}

          <button
            className="check-customer-btn"
            onClick={
              checkCustomerDetails
            }
            disabled={
              checkingCustomer ||
              pin.length !== 4
            }
            type="button"
          >

            {checkingCustomer ? (

              <>
                <SyncOutlined spin />

                {" "}

                VERIFYING CUSTOMER...
              </>

            ) : (

              <>
                <SafetyOutlined />

                {" "}

                CHECK CUSTOMER DETAILS
              </>

            )}

          </button>

          {/* ==================================================
              SECURITY NOTE
          ================================================== */}

          <div className="pin-security-note">

            <SafetyOutlined />

            {" "}

            The QR code and PIN are
            verified by the server before
            customer information is displayed.

          </div>

        </div>

      </Modal>
    </>
  );
};

export default RoyaltyATMScan;
